// Libs
import { Suspense, lazy } from 'react';
import { LoaderFunctionArgs, RouteObject, redirect } from 'react-router';

// Components
import ErrorDisplay from '~fpu/shared/components/ErrorDisplay';
import OrgAdminLayout from './OrgLayout';
import Header from '~fpu/shared/components/navigation/Header';
import RouteErrorElement from '~fpu/shared/components/RouteErrorElement';
import { PageAnalyticsWrapper } from '~fpu/shared/utils/PageAnalyticsWrapper';

// Lazy-loaded Components
const AdminRedirect = lazy(() => import('./components/AdminRedirect'));
const MembershipInsights = lazy(
  () =>
    import('~fpu/modules/FpuDashboard/components/MembershipInsights/MembershipInsights')
);

const OrganizationAdmin = lazy(
  () => import('./components/OrganizationAdminHome/OrganizationAdmin')
);
const OrganizationClasses = lazy(
  () => import('./components/OrganizationClasses')
);
const OrganizationMemberships = lazy(
  () =>
    import('~fpu/modules/FpuDashboard/components/MyMemberships/OrganizationMemberships')
);
const OrganizationUniqueClassPage = lazy(
  () =>
    import('./components/OrganizationUniqueClassPage/OrganizationUniqueClassPage')
);
const Redeem = lazy(() => import('./components/Redeem'));
const JoinOrgClassInterstitial = lazy(
  () => import('~fpu/shared/components/JoinOrgClassInterstitial')
);
const RedemptionFlowActivation = lazy(
  () => import('../FpuDashboard/components/RedemptionFlowActivation')
);
const LaunchAndPromote = lazy(
  () => import('../FpuDashboard/components/LaunchAndPromote')
);
const Testimonial = lazy(
  () => import('~fpu/modules/FpuDashboard/components/Testimonials/RealStories')
);

const ErrorElement = () => (
  <RouteErrorElement>
    {(status) => (
      <OrgAdminLayout>
        <ErrorDisplay code={status} />
      </OrgAdminLayout>
    )}
  </RouteErrorElement>
);

// Error element for when user may not be an organization admin
const NonAdminErrorElement = () => (
  <RouteErrorElement>
    {(status) => (
      <>
        <Header />
        <ErrorDisplay code={status} />
      </>
    )}
  </RouteErrorElement>
);

// Redirects old organization IDs to new organization IDs (e.g. PEACE-1234 -> CRUSADE-1234)
function createLoaderWithOrganizationIdRedirect(redirectPath: string) {
  return async ({ params }: LoaderFunctionArgs) => {
    const organizationId = params.organizationId as string;
    const isOldOrganizationId = /^peace-/i.test(organizationId);

    if (isOldOrganizationId) {
      const newOrgId = organizationId.replace(/^peace-/i, 'CRUSADE-');
      return redirect(`${redirectPath}/${newOrgId}`);
    }

    return null;
  };
}

const ORGANIZATION_ROUTES: RouteObject[] = [
  {
    path: 'organization',
    errorElement: <ErrorElement />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={null}>
            <AdminRedirect />
          </Suspense>
        )
      },
      {
        path: ':organizationId',
        children: [
          {
            index: true,
            loader: createLoaderWithOrganizationIdRedirect('/organization'),
            element: (
              <Suspense fallback={null}>
                <OrganizationAdmin />
              </Suspense>
            )
          },
          {
            path: 'classes',
            element: (
              <Suspense fallback={null}>
                <OrgAdminLayout>
                  <OrganizationClasses />
                </OrgAdminLayout>
              </Suspense>
            )
          },
          {
            path: 'memberships',
            element: (
              <Suspense fallback={null}>
                <OrgAdminLayout>
                  <OrganizationMemberships />
                </OrgAdminLayout>
              </Suspense>
            )
          },
          {
            path: 'insights',
            element: (
              <Suspense fallback={null}>
                <OrgAdminLayout>
                  <PageAnalyticsWrapper
                    category="Organization"
                    page="Membership Insights"
                  >
                    <MembershipInsights />
                  </PageAnalyticsWrapper>
                </OrgAdminLayout>
              </Suspense>
            )
          },
          {
            path: 'promote',
            element: (
              <Suspense fallback={null}>
                <OrgAdminLayout>
                  <LaunchAndPromote />
                </OrgAdminLayout>
              </Suspense>
            )
          },
          {
            path: 'testimonials',
            element: (
              <Suspense fallback={null}>
                <OrgAdminLayout>
                  <PageAnalyticsWrapper
                    category="Organization"
                    page="Testimonials"
                  >
                    <Testimonial />
                  </PageAnalyticsWrapper>
                </OrgAdminLayout>
              </Suspense>
            )
          }
        ]
      },
      {
        path: 'activation/:organizationId',
        errorElement: <NonAdminErrorElement />,
        element: (
          <Suspense fallback={null}>
            <RedemptionFlowActivation />
          </Suspense>
        )
      },
      {
        path: 'redeem/:organizationId',
        loader: createLoaderWithOrganizationIdRedirect('/organization/redeem'),
        errorElement: <NonAdminErrorElement />,
        element: (
          <Suspense fallback={null}>
            <Redeem />
          </Suspense>
        )
      },
      {
        path: 'redeem/:organizationId/classes',
        errorElement: <NonAdminErrorElement />,
        element: (
          <Suspense fallback={null}>
            <JoinOrgClassInterstitial />
          </Suspense>
        )
      },
      {
        // Organization UCP
        path: 'classes/:organizationId/:classCode',
        errorElement: <NonAdminErrorElement />,
        element: (
          <Suspense fallback={null}>
            <OrganizationUniqueClassPage />
          </Suspense>
        )
      }
    ]
  }
];

export default ORGANIZATION_ROUTES;
